import { Link, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import type { MethodAmount, ReportPaymentMethod } from '@/lib/api/report-api'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useCashFlow } from '@/hooks/use-report-query'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/accounting/cash-flow',
)({
    component: CashFlowPage,
})

function methodLabel(method: string): string {
    switch (method) {
        case 'cash':
            return m.cashflow_method_cash()
        case 'bank_transfer':
            return m.cashflow_method_bank_transfer()
        case 'card':
            return m.cashflow_method_card()
        // Money the carrier collected on delivery: real revenue, but a
        // receivable from the carrier until their remittance clears — so it
        // must not read as an unlabelled "other".
        case 'cod_via_carrier':
            return m.cashflow_method_cod_via_carrier()
        default:
            return m.cashflow_method_other()
    }
}

function sum(rows: MethodAmount[]): number {
    return rows.reduce((s, r) => s + r.amount, 0)
}

function MethodTable({ title, rows }: { title: string; rows: MethodAmount[] }) {
    return (
        <div className="space-y-2">
            <h2 className="font-semibold">{title}</h2>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.cashflow_method()}</TableHead>
                        <TableHead className="text-right">
                            {m.cashflow_amount()}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={2}
                                className="text-center text-muted-foreground py-4"
                            >
                                {m.cashflow_empty()}
                            </TableCell>
                        </TableRow>
                    )}
                    {rows.map((r) => (
                        <TableRow key={r.method}>
                            <TableCell>{methodLabel(r.method)}</TableCell>
                            <TableCell className="text-right font-mono">
                                {formatVnd(r.amount)}
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow className="border-t-2 font-semibold">
                        <TableCell>{m.cashflow_total()}</TableCell>
                        <TableCell className="text-right font-mono">
                            {formatVnd(sum(rows))}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    )
}

function StatCard({ label, value }: { label: string; value: number }) {
    return (
        <div className="rounded border p-3">
            <div className="text-xs text-muted-foreground">{label}</div>
            <div
                className={`font-mono text-lg font-semibold ${
                    value < 0 ? 'text-red-600' : ''
                }`}
            >
                {formatVnd(value)}
            </div>
        </div>
    )
}

function CashFlowPage() {
    const [dateFrom, setDateFrom] = useState('')
    const [dateTo, setDateTo] = useState('')
    const [method, setMethod] = useState<ReportPaymentMethod | ''>('')

    const { data } = useCashFlow({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        payment_method: method || undefined,
    })

    return (
        <div className="space-y-4 p-4">
            <div>
                <h1 className="text-xl font-semibold">{m.cashflow_title()}</h1>
                <p className="text-sm text-muted-foreground">
                    {m.cashflow_subtitle()}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 max-w-2xl">
                <div>
                    <Label className="text-xs">
                        {m.cashflow_filter_from()}
                    </Label>
                    <Input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                    />
                </div>
                <div>
                    <Label className="text-xs">{m.cashflow_filter_to()}</Label>
                    <Input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                    />
                </div>
                <div>
                    <Label className="text-xs">
                        {m.expense_payment_method()}
                    </Label>
                    <Select
                        value={method || 'all'}
                        onValueChange={(v) =>
                            setMethod(
                                v === 'cash' || v === 'bank_transfer' ? v : '',
                            )
                        }
                    >
                        <SelectTrigger>
                            <SelectValue
                                placeholder={m.expense_filter_all_methods()}
                            />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">
                                {m.expense_filter_all_methods()}
                            </SelectItem>
                            <SelectItem value="cash">
                                {m.expense_payment_method_cash()}
                            </SelectItem>
                            <SelectItem value="bank_transfer">
                                {m.expense_payment_method_bank_transfer()}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <MethodTable
                    title={m.cashflow_inflow()}
                    rows={data?.inflow_by_method ?? []}
                />
                <MethodTable
                    title={m.cashflow_outflow()}
                    rows={data?.outflow_by_method ?? []}
                />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <StatCard label={m.cashflow_net()} value={data?.net ?? 0} />
                <StatCard
                    label={m.cashflow_cash_net()}
                    value={data?.cash_net ?? 0}
                />
                <StatCard
                    label={m.cashflow_bank_net()}
                    value={data?.bank_net ?? 0}
                />
            </div>

            <div className="rounded border p-3 space-y-2">
                <h2 className="font-semibold">{m.cashflow_recon_title()}</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <StatCard
                        label={m.cashflow_bank_actual()}
                        value={data?.bank_actual ?? 0}
                    />
                    <StatCard
                        label={m.cashflow_bank_recorded()}
                        value={data?.bank_recorded ?? 0}
                    />
                    <StatCard
                        label={m.cashflow_bank_diff()}
                        value={data?.bank_difference ?? 0}
                    />
                </div>
                {(data?.bank_difference ?? 0) > 0 && (
                    <Link
                        to="/accounting/reconciliation"
                        className="inline-block text-sm text-primary underline"
                    >
                        {m.cashflow_bank_diff_link()}
                    </Link>
                )}
            </div>

            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm space-y-1">
                <div className="font-semibold">{m.cashflow_caveat_title()}</div>
                <ul className="list-disc pl-5 text-muted-foreground">
                    <li>{m.cashflow_caveat_cod()}</li>
                    <li>{m.cashflow_caveat_cancelled()}</li>
                    <li>{m.cashflow_caveat_outflow()}</li>
                    <li>{m.cashflow_caveat_bankdiff()}</li>
                </ul>
            </div>
        </div>
    )
}
